import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { Dash } from "@/components/Dash";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";
import { toEditorMarkdown } from "@/components/RichTextEditor";
import { useInlineImagePreview } from "@/hooks/useInlineImagePreview";
import {
  TicketStatusPill,
  PriorityBadge,
  TICKET_STATUS_OPTIONS,
  TICKET_PRIORITY_OPTIONS,
} from "@/features/tickets/components/TicketBadges";
import { ApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { toApiDateTime, dateOnly, formatDateTime } from "@/utils/dates";
import {
  tickets as ticketsApi,
  type TicketAttachmentList,
  type TicketDetail,
} from "@/features/tickets/api/tickets.api";
import {
  TICKET_MAIN_CATEGORY_OPTIONS,
  ticketCategoryOptions,
  withCurrentValue,
} from "@/features/tickets/categories";
import { useBackOrigin, useBackTo } from "@/hooks/useBackTo";
import { TemplateInsert } from "@/features/tickets/components/TemplateInsert";
import { appendMarkdown } from "@/components/RichTextEditor";
import {
  anyUrlExpiresBefore,
  deflateInlineImages,
  inflateInlineImages,
  inlineImageIds,
  invertUrlById,
  URL_REFRESH_MARGIN_MS,
  refreshInlineImages,
} from "@/utils/inlineImages";
import {
  conversations as conversationsApi,
  ATTACHMENT_ERROR_UPLOAD,
  deleteConfirmCopy,
  MessageActions,
  MessageCard,
  MessageComposer,
  MessageEditor,
  MESSAGE_DELETE_ERROR,
  MESSAGE_GONE_ERROR,
  type ComposerSubmit,
  type ConversationMessage,
} from "@/features/conversations";
import { useAuth } from "@/features/auth/auth";
import { useConfirm } from "@/components/ConfirmDialog";
import { Notice } from "@/components/Notice";
import { FEATURES } from "@/config/featureFlags";
import { TicketAttachmentsCard } from "@/features/tickets/components/TicketAttachmentsCard";
import { AssigneeField } from "@/features/tickets/components/AssigneeField";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { TextField, SelectField, RichTextField, DateField, CheckField, useUnsavedChangesWarning } from "@/components/form";

const DISCARD_TICKET_CHANGES = {
  title: "Discard unsaved changes?",
  description: "You have unsaved changes on this ticket. Leaving discards them.",
  confirmLabel: "Discard",
  cancelLabel: "Stay",
  destructive: true,
} as const;

async function loadAttachments(ticketId: string): Promise<TicketAttachmentList> {
  try {
    return await ticketsApi.listAttachments(ticketId);
  } catch {
    return { totalCount: 0, items: [] };
  }
}

async function imageUrlsFor(
  body: string,
  list: TicketAttachmentList,
): Promise<Map<string, string>> {
  const urls = new Map<string, string>();
  for (const a of list.items) {
    if (a.url) urls.set(a.id, a.url);
  }
  for (const missing of inlineImageIds(body).filter((refId) => !urls.has(refId))) {
    try {
      const url = await ticketsApi.attachmentUrl(missing);
      if (url) urls.set(missing, url);
    } catch {
      continue;
    }
  }
  return urls;
}

const URL_REFRESH_POLL_MS = 60 * 1000;

function expiringSoon(items: TicketAttachmentList["items"]): boolean {
  const cutoff = Date.now() + URL_REFRESH_MARGIN_MS;
  return items.some((a) => {
    if (!a.url || !a.urlExpiresAt) return false;
    const expiry = new Date(a.urlExpiresAt).getTime();
    return Number.isFinite(expiry) && expiry <= cutoff;
  });
}

const THREAD_TICK =
  "relative before:absolute before:-left-8 before:-top-4 before:w-px before:bg-slate-200 " +
  "after:absolute after:-left-8 after:top-4 after:h-px after:w-8 after:bg-slate-200";

const schema = z.object({
  subject: z.string().trim().min(1, "A ticket subject is required.").max(FIELD_MAX.ticket.subject, tooLong("Subject", FIELD_MAX.ticket.subject)),
  body: z.string(),
  status: z.string(),
  priority: z.string(),
  mainCategory: z.string(),
  category: z.string(),
  assignedToId: z.string(),
  hideFromMerchant: z.boolean(),
  notifyMerchant: z.boolean(),

  dueAt: z.string(),
});
type FormState = z.infer<typeof schema>;

const BLANK: FormState = {
  subject: "", body: "", status: "", priority: "", mainCategory: "", category: "",
  assignedToId: "", hideFromMerchant: false, notifyMerchant: false, dueAt: "",
};

function toForm(t: TicketDetail, imageUrls: Map<string, string>): FormState {
  return {
    subject: t.subject,
    body: inflateInlineImages(toEditorMarkdown(t.body, t.bodyFormat), imageUrls),
    status: t.status,
    priority: t.priority,
    mainCategory: t.mainCategory ?? "",
    category: t.category ?? "",
    assignedToId: t.assignedTo?.id ?? "",
    hideFromMerchant: t.hideFromMerchant,
    notifyMerchant: t.notifyMerchant,

    dueAt: dateOnly(t.dueAt),
  };
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const confirm = useConfirm();

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<string | null>(null);

  const [replyTo, setReplyTo] = useState<ConversationMessage | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [messageError, setMessageError] = useState<{ id: string; message: string } | null>(null);
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);

  const [attachments, setAttachments] = useState<TicketAttachmentList>({
    totalCount: 0,
    items: [],
  });
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const [templateInsertedAt, setTemplateInsertedAt] = useState(0);
  const [remoteMessages, setRemoteMessages] = useState<ConversationMessage[] | null>(null);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const conversationId = ticket?.conversationId ?? null;

  const form = useForm<FormState>({
    resolver: zodResolver(schema),
    defaultValues: BLANK,
  });
  const { isDirty, isSubmitting } = form.formState;
  const backTo = useBackTo("/tickets");
  const merchantOrigin = useBackOrigin();
  const navigate = useNavigate();
  useUnsavedChangesWarning(isDirty);

  async function leaveBack(e: MouseEvent<HTMLAnchorElement>) {
    if (!isDirty || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    const discard = await confirm(DISCARD_TICKET_CHANGES);
    if (discard) navigate(backTo);
  }

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const [t, list] = await Promise.all([ticketsApi.get(id), loadAttachments(id)]);
        const urls = await imageUrlsFor(toEditorMarkdown(t.body, t.bodyFormat), list);
        if (cancelled) return;
        setTicket(t);
        setAttachments(list);
        setImageUrls(urls);
        form.reset(toForm(t, urls));
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) {
          setForbidden(e.message || "You do not have permission to perform this action.");
        }
        else if (e instanceof ApiError && e.status === 404) setError("Ticket not found.");
        else setError(e instanceof Error ? e.message : "Failed to load the ticket.");
      }
    })();
    return () => { cancelled = true; };
  }, [id, form]);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    setConversationError(null);
    conversationsApi.listMessages(conversationId, { limit: 100 })
      .then((page) => { if (!cancelled) setRemoteMessages(page.items); })
      .catch((e) => {
        if (cancelled) return;
        setConversationError(
          e instanceof Error ? e.message : "Failed to load the conversation.",
        );
      });
    return () => { cancelled = true; };
  }, [conversationId]);

  useEffect(() => {
    if (!pendingScrollId) return;
    const el = document.querySelector(`[data-message-id="${pendingScrollId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setPendingScrollId(null);
  }, [remoteMessages, pendingScrollId]);

  const mainCategory = form.watch("mainCategory");
  const category = form.watch("category");
  const mainCategoryOptions = useMemo(
    () => withCurrentValue(TICKET_MAIN_CATEGORY_OPTIONS, ticket?.mainCategory),
    [ticket?.mainCategory],
  );
  const categoryOptions = useMemo(
    () =>
      withCurrentValue(
        ticketCategoryOptions(mainCategory),
        mainCategory === (ticket?.mainCategory ?? "") ? ticket?.category : null,
      ),
    [mainCategory, ticket?.mainCategory, ticket?.category],
  );

  useEffect(() => {
    if (!category) return;
    if (categoryOptions.some(([v]) => v === category)) return;
    form.setValue("category", "", { shouldDirty: true });
  }, [category, categoryOptions, form]);

  async function save(v: FormState) {
    if (!id) return;
    if (!isDirty) {
      toast.info("No changes to save");
      return;
    }
    const body = deflateInlineImages(v.body, invertUrlById(imageUrls));
    if (body.length > FIELD_MAX.ticket.body) {
      form.setError("body", {
        message: tooLong("Description", FIELD_MAX.ticket.body),
      });
      onInvalid();
      return;
    }
    setError(null);
    try {
      const updated = await ticketsApi.update(id, {
        subject: v.subject.trim(),
        mainCategory: v.mainCategory.trim() || null,
        category: v.category.trim() || null,
        priority: v.priority,
        status: v.status,
        body,
        bodyFormat: "Markdown",
        assignedToId: v.assignedToId || null,
        hideFromMerchant: v.hideFromMerchant,
        notifyMerchant: v.notifyMerchant,
        dueAt: toApiDateTime(v.dueAt),
      });
      setTicket(updated);
      form.reset(toForm(updated, imageUrls));
      await reloadAttachments();
      toast.success("Ticket updated");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save the ticket.");
    }
  }

  const refreshIfStale = useRef(() => {});
  refreshIfStale.current = () => {
    if (!id || document.visibilityState !== "visible") return;
    const deadline = Date.now() + URL_REFRESH_MARGIN_MS;
    const messageUrls = (remoteMessages ?? []).flatMap((m) =>
      m.attachments.flatMap((a) => (a.url ? [a.url] : [])),
    );
    if (conversationId && anyUrlExpiresBefore(messageUrls, deadline)) {
      void reloadMessages(conversationId);
      return;
    }
    if (expiringSoon(attachments.items)) void reloadAttachments();
  };

  useEffect(() => {
    const run = () => refreshIfStale.current();
    window.addEventListener("visibilitychange", run);
    window.addEventListener("focus", run);
    const timer = window.setInterval(run, URL_REFRESH_POLL_MS);
    return () => {
      window.removeEventListener("visibilitychange", run);
      window.removeEventListener("focus", run);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (imageUrls.size === 0) return;
    const deadline = Date.now() + URL_REFRESH_MARGIN_MS;
    const defaults = (form.formState.defaultValues ?? BLANK) as FormState;
    const saved = defaults.body ?? "";
    const current = form.getValues("body");
    const nextSaved = refreshInlineImages(saved, imageUrls, deadline);
    const nextCurrent = refreshInlineImages(current, imageUrls, deadline);
    if (nextSaved === saved && nextCurrent === current) return;
    form.reset({ ...defaults, body: nextSaved }, {
      keepDirtyValues: true,
      keepErrors: true,
      keepTouched: true,
    });
    if (nextCurrent !== current) {
      form.setValue("body", nextCurrent, { shouldDirty: current !== saved });
    }
  }, [imageUrls, form]);

  function insertTemplate(content: string) {
    form.setValue("body", appendMarkdown(form.getValues("body"), content), {
      shouldDirty: true,
    });
    setTemplateInsertedAt((n) => n + 1);
  }

  function onInvalid() {
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      el?.focus({ preventScroll: true });
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  async function reloadAttachments() {
    if (!id) return;
    const list = await loadAttachments(id);
    setAttachments(list);
    setImageUrls((prev) => {
      const next = new Map(prev);
      for (const a of list.items) {
        if (a.url) next.set(a.id, a.url);
      }
      return next;
    });
  }

  const idByUrl = useMemo(() => invertUrlById(imageUrls), [imageUrls]);
  const loadAttachment = useCallback((aid: string) => ticketsApi.attachmentFile(aid), []);
  const previewInlineImage = useInlineImagePreview(idByUrl, loadAttachment);

  async function uploadInlineImage(file: File) {
    if (!id) throw new Error("Save the ticket before adding an image.");
    const image = await ticketsApi.uploadInlineImage(id, file);
    setImageUrls((prev) => new Map(prev).set(image.id, image.url));
    void reloadAttachments();
    return image;
  }

  async function uploadMessageImage(file: File) {
    const image = await ticketsApi.uploadInlineImage(null, file);
    setImageUrls((prev) => new Map(prev).set(image.id, image.url));
    return image;
  }

  async function reloadMessages(cid: string) {
    const page = await conversationsApi.listMessages(cid, { limit: 100 });
    setRemoteMessages(page.items);
    setMessageError(null);
    setConversationError(null);
    await reloadAttachments();
  }

  function jumpToMessage(messageId: string) {
    const target = (remoteMessages ?? []).find((m) => m.id === messageId);
    if (target?.parentId) setExpandedReplies((prev) => new Set(prev).add(target.parentId as string));
    setPendingScrollId(messageId);
  }

  async function sendMessage(
    payload: ComposerSubmit,
    parentId: string | null,
  ): Promise<ConversationMessage | null> {
    if (!conversationId) return null;
    const input = {
      body: payload.body,
      parentId,
      isInternal: payload.isInternal,
      bodyFormat: "Markdown" as const,
    };
    return payload.files.length
      ? conversationsApi.postMessageWithFiles(conversationId, input, payload.files)
      : conversationsApi.postMessage(conversationId, input);
  }

  async function postComment(payload: ComposerSubmit) {
    if (!conversationId) return null;
    try {
      await sendMessage(payload, null);
      await reloadMessages(conversationId);
      return null;
    } catch (e) {
      return failureMessage(e, payload.files.length > 0, "Failed to post the comment.");
    }
  }

  function toggleReplies(commentId: string) {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  }

  function startReply(m: ConversationMessage) {
    setEditingId(null);
    setReplyTo(m);
    setExpandedReplies((prev) => new Set(prev).add(m.id));
  }

  function cancelReply() {
    setReplyTo(null);
  }

  function startEdit(m: ConversationMessage) {
    setReplyTo(null);
    setEditingId(m.id);
  }

  function isAuthor(m: ConversationMessage) {
    return !!user && !!m.authorId && m.authorId === user.userId;
  }

  async function afterEditSaved() {
    setEditingId(null);
    if (conversationId) await reloadMessages(conversationId);
  }

  async function removeMessage(m: ConversationMessage) {
    if (!conversationId) return;
    setMessageError(null);
    try {
      const { deletedIds } = await conversationsApi.deleteMessage(m.id);
      const gone = new Set(deletedIds.length ? deletedIds : [m.id]);
      if (editingId && gone.has(editingId)) setEditingId(null);
      if (replyTo && gone.has(replyTo.id)) setReplyTo(null);
      setExpandedReplies((prev) => {
        const next = new Set(prev);
        for (const id of gone) next.delete(id);
        return next;
      });
      setRemoteMessages((prev) => {
        const list = prev ?? [];
        const removed = list.filter((x) => gone.has(x.id));
        return list
          .filter((x) => !gone.has(x.id))
          .map((x) => {
            const lost = removed.filter((r) => r.parentId === x.id).length;
            return lost === 0 ? x : { ...x, replyCount: Math.max(0, x.replyCount - lost) };
          });
      });
      await reloadAttachments();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setError(MESSAGE_GONE_ERROR);
        await reloadMessages(conversationId).catch(() => {});
        return;
      }
      setMessageError({
        id: m.id,
        message: e instanceof ApiError && e.status === 403 ? e.message : MESSAGE_DELETE_ERROR,
      });
    }
  }

  function askDelete(m: ConversationMessage, isReply: boolean, replyCount: number) {
    void confirm({
      ...deleteConfirmCopy({ isReply, isInternal: m.isInternal, replyCount }),
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      destructive: true,
      onConfirm: () => removeMessage(m),
    });
  }

  async function postReply(payload: ComposerSubmit) {
    if (!conversationId || !replyTo) return null;
    try {
      const created = await sendMessage(
        { ...payload, isInternal: replyTo.isInternal || payload.isInternal },
        replyTo.id,
      );
      await reloadMessages(conversationId);
      if (created) setPendingScrollId(created.id);
      setReplyTo(null);
      return null;
    } catch (e) {
      return failureMessage(e, payload.files.length > 0, "Failed to post the reply.");
    }
  }

  if (forbidden) {
    return (
      <>
        <Notice tone="warn">{forbidden}</Notice>
        <Link to={backTo} className="text-sm text-muted-foreground hover:underline mt-3 inline-block">
          ← Back
        </Link>
      </>
    );
  }
  if (error && !ticket) {
    return (
      <>
        <Notice tone="error">{error}</Notice>
        <Link to={backTo} className="text-sm text-muted-foreground hover:underline mt-3 inline-block">
          ← Back
        </Link>
      </>
    );
  }
  if (!ticket) {
    return (
      <>
        <Loading>Loading ticket…</Loading>
      </>
    );
  }

  const { topLevel: topLevelComments, repliesByParent } = groupMessages(remoteMessages ?? []);

  return (
    <>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Link to={backTo} onClick={leaveBack} className="text-xs text-muted-foreground hover:underline">
            ← Back
          </Link>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-1">
            <h1 className="min-w-0 break-words text-2xl font-semibold text-slate-800">
              <span className="font-mono text-slate-400">#{ticket.ticketNumber}</span>{" "}
              {ticket.subject}
            </h1>
            <TicketStatusPill status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
          </div>
        </div>
        <Button
          type="button"
          disabled={isSubmitting}
          onClick={form.handleSubmit(save, onInvalid)}
          className="shrink-0 self-start"
        >
          {isSubmitting ? "Saving…" : "Save changes"}
        </Button>
      </header>

      {error && <div className="mb-4"><Notice tone="error">{error}</Notice></div>}

      <Form {...form}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="min-w-0 lg:col-span-2 space-y-6">
            <Panel title="Ticket information">
              <TextField control={form.control} name="subject" label="Subject" required />
              <RichTextField
                control={form.control}
                name="body"
                label="Description"
                placeholder="Describe the issue…"
                above={<TemplateInsert onInsert={insertTemplate} />}
                revealEndSignal={templateInsertedAt}
                uploadImage={uploadInlineImage}
                previewImage={previewInlineImage}
              />
            </Panel>

            <Card>
              <CardHeader className="border-b py-3">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Conversation
                  {remoteMessages !== null && (
                    <span className="ml-2 text-sm font-normal text-slate-400">
                      {remoteMessages.length} comment{remoteMessages.length === 1 ? "" : "s"}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {!conversationId ? (
                  <p className="text-sm text-slate-400 mb-4">
                    This ticket has no conversation, so comments are unavailable.
                  </p>
                ) : conversationError ? (
                  <div className="mb-4">
                    <Notice tone="error">{conversationError}</Notice>
                  </div>
                ) : remoteMessages === null ? (
                  <p className="text-sm text-slate-400 mb-4">Loading conversation…</p>
                ) : topLevelComments.length === 0 ? (
                  <p className="text-sm text-slate-400 mb-4">No comments yet.</p>
                ) : (
                  <ul className="space-y-6 mb-6">
                    {topLevelComments.map((c) => {
                      const replies = repliesByParent.get(c.id) ?? [];
                      const repliesOpen = expandedReplies.has(c.id);
                      const composerOpen = replyTo?.id === c.id;
                      const threadOpen = (repliesOpen && replies.length > 0) || composerOpen;
                      return (
                        <li key={c.id}>
                          <MessageCard
                            message={c}
                            internal={c.isInternal}
                            spine={threadOpen}
                            editing={
                              editingId === c.id ? (
                                <MessageEditor
                                  message={c}
                                  insertControl={(append) => <TemplateInsert onInsert={append} />}
                                  uploadImage={uploadMessageImage}
                                  previewImage={previewInlineImage}
                                  onCancel={() => setEditingId(null)}
                                  onSaved={afterEditSaved}
                                />
                              ) : undefined
                            }
                            actions={
                              editingId === c.id ? undefined : (
                                <MessageActions
                                  onReply={c.isDeleted ? undefined : () => startReply(c)}
                                  onEdit={
                                    !c.isDeleted && isAuthor(c) ? () => startEdit(c) : undefined
                                  }
                                  onDelete={
                                    !c.isDeleted && isAuthor(c)
                                      ? () =>
                                          askDelete(
                                            c,
                                            false,
                                            Math.max(c.replyCount, replies.length),
                                          )
                                      : undefined
                                  }
                                  replies={
                                    replies.length > 0
                                      ? {
                                          count: replies.length,
                                          open: repliesOpen,
                                          onToggle: () => toggleReplies(c.id),
                                        }
                                      : undefined
                                  }
                                />
                              )
                            }
                          >
                            <MessageError messageId={c.id} error={messageError} />

                            {threadOpen && (
                              <div className="mt-4 space-y-4 pl-1">
                                {repliesOpen &&
                                  replies.map((r, i) => (
                                    <div
                                      key={r.id}
                                      className={cn(
                                        THREAD_TICK,
                                        i === replies.length - 1 && !composerOpen
                                          ? "before:h-8"
                                          : "before:bottom-0",
                                      )}
                                    >
                                      <MessageCard
                                        compact
                                        message={r}
                                        internal={r.isInternal}
                                        editing={
                                          editingId === r.id ? (
                                            <MessageEditor
                                              message={r}
                                              rows={2}
                                              insertControl={(append) => (
                                                <TemplateInsert onInsert={append} />
                                              )}
                                              uploadImage={uploadMessageImage}
                                              previewImage={previewInlineImage}
                                              onCancel={() => setEditingId(null)}
                                              onSaved={afterEditSaved}
                                            />
                                          ) : undefined
                                        }
                                        actions={
                                          editingId === r.id ? undefined : (
                                            <MessageActions
                                              onEdit={
                                                !r.isDeleted && isAuthor(r)
                                                  ? () => startEdit(r)
                                                  : undefined
                                              }
                                              onDelete={
                                                !r.isDeleted && isAuthor(r)
                                                  ? () => askDelete(r, true, 0)
                                                  : undefined
                                              }
                                            />
                                          )
                                        }
                                      >
                                        <MessageError messageId={r.id} error={messageError} />
                                      </MessageCard>
                                    </div>
                                  ))}

                                {composerOpen && (
                                  <div className={cn(THREAD_TICK, "before:h-8")}>
                                    <MessageComposer
                                      autoFocus
                                      placeholder="Write a reply…"
                                      submitLabel="Save"
                                      busyLabel="Saving…"
                                      internalLocked={c.isInternal}
                                      internalLockedHint="Replies to an internal note stay internal."
                                      insertControl={(append) => <TemplateInsert onInsert={append} />}
                                      uploadImage={uploadMessageImage}
                                  previewImage={previewInlineImage}
                                      onCancel={cancelReply}
                                      onSubmit={postReply}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </MessageCard>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {conversationId && (
                  <MessageComposer
                    placeholder="Add a comment…"
                    submitLabel="Post comment"
                    busyLabel="Posting…"
                    insertControl={(append) => <TemplateInsert onInsert={append} />}
                    uploadImage={uploadMessageImage}
                                  previewImage={previewInlineImage}
                    onSubmit={postComment}
                  />
                )}
              </CardContent>
            </Card>

            <TicketAttachmentsCard
              ticketId={ticket.id}
              attachments={attachments.items}
              totalCount={attachments.totalCount}
              onChanged={reloadAttachments}
              onJumpToMessage={jumpToMessage}
            />
          </div>

          <aside className="space-y-6">
            <Panel title="Properties">
              <SelectField control={form.control} name="status" label="Status" options={TICKET_STATUS_OPTIONS} />
              <SelectField control={form.control} name="priority" label="Priority" options={TICKET_PRIORITY_OPTIONS} />
              <SelectField
                control={form.control}
                name="mainCategory"
                label="Main Category"
                placeholder="Select Main Category"
                options={mainCategoryOptions}
              />
              <SelectField
                control={form.control}
                name="category"
                label="Category"
                placeholder={mainCategory ? "Select Category" : "Select Main Category first"}
                options={categoryOptions}
                searchable
                disabled={categoryOptions.length === 0}
              />

              <AssigneeField
                control={form.control}
                name="assignedToId"
                current={ticket.assignedTo}
              />

              <DateField control={form.control} name="dueAt" label="Due date" hint="Click the selected day again to clear the deadline." />

              {FEATURES.ticketMerchantOptions && (
                <>
                  <CheckField control={form.control} name="hideFromMerchant" label="Hide from merchant" />
                  <CheckField control={form.control} name="notifyMerchant" label="Notify merchant" />
                </>
              )}
            </Panel>

            <Panel title="Merchant">
              <div className="text-sm font-medium text-slate-900">
                {ticket.merchantDbaName || <Dash />}
              </div>
              {ticket.merchantMid && (
                <div className="text-xs text-slate-500 font-mono">{ticket.merchantMid}</div>
              )}
              <Link
                to={`/merchants/${ticket.merchantId}`}
                state={merchantOrigin}
                className="inline-block text-xs text-brand-600 hover:underline"
              >
                View merchant →
              </Link>
            </Panel>

            <Panel title="Activity">
              <Meta label="Created by" value={ticket.createdBy?.name || <Dash />} />
              <Meta label="Created" value={formatDateTime(ticket.createdAt)} />
              <Meta label="Last updated" value={formatDateTime(ticket.updatedAt)} />
              {ticket.closedAt && <Meta label="Closed" value={formatDateTime(ticket.closedAt)} />}
            </Panel>
          </aside>
        </div>
      </Form>
    </>
  );
}

function MessageError({
  messageId,
  error,
}: {
  messageId: string;
  error: { id: string; message: string } | null;
}) {
  if (error?.id !== messageId) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-red-600">
      {error.message}
    </p>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-3">{children}</CardContent>
    </Card>
  );
}

function failureMessage(e: unknown, hadFiles: boolean, fallback: string) {
  if (e instanceof ApiError && (e.status === 400 || e.status === 403)) return e.message;
  if (hadFiles) return ATTACHMENT_ERROR_UPLOAD;
  return e instanceof Error ? e.message : fallback;
}

function groupMessages(messages: ConversationMessage[]) {
  const sorted = [...messages].sort((a, b) => a.seq - b.seq);
  const ids = new Set(sorted.map((m) => m.id));

  const repliesByParent = new Map<string, ConversationMessage[]>();
  const topLevel: ConversationMessage[] = [];
  for (const m of sorted) {
    const anchorId =
      m.depth > 0 && m.rootId && m.rootId !== m.id && ids.has(m.rootId) ? m.rootId : null;
    if (anchorId) {
      const arr = repliesByParent.get(anchorId);
      if (arr) arr.push(m);
      else repliesByParent.set(anchorId, [m]);
    } else {
      topLevel.push(m);
    }
  }
  return { topLevel, repliesByParent };
}

function Meta({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-28 shrink-0 text-slate-500">{label}</dt>
      <dd className="text-slate-900">{value}</dd>
    </div>
  );
}
